/**
 * 图片模型选择器
 * 顶部一排公司 logo 选项，选中哪家公司就只展示该公司下的模型；
 * 点击公司默认选中该公司第一个模型。
 */

import { useEffect, useMemo, useState } from 'react';
import { Activity, Check } from 'lucide-react';
import type { ModelConfig } from './types';
import { CompanyLogo } from '../../components/CompanyLogo';

interface ModelSelectorProps {
  value: string;
  onChange: (value: string) => void;
  models: ModelConfig[];
}

interface ModelCompanyGroup {
  key: string;
  label: string;
  models: ModelConfig[];
}

/**
 * 按公司分组；组间顺序跟随模型 sortOrder 首次出现的顺序（管理员可通过排序控制），
 * 无公司信息的模型归入「其他」兜底组。
 */
function groupModelsByCompany(models: ModelConfig[]): ModelCompanyGroup[] {
  const groups: ModelCompanyGroup[] = [];
  const byKey = new Map<string, ModelCompanyGroup>();
  for (const model of models) {
    const key = model.company?.id || 'other';
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: model.company?.label || '其他', models: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.models.push(model);
  }
  return groups;
}

/** 可用率徽章配色：>=90% 绿、75-90% 橙、<75% 红 */
function availabilityTone(rate: number): string {
  if (rate >= 90) return 'text-accent-green bg-accent-green/10';
  if (rate >= 75) return 'text-accent-yellow bg-accent-yellow/10';
  return 'text-red-500 bg-red-500/10';
}

export function ModelSelector({ value, onChange, models }: ModelSelectorProps) {
  const groups = useMemo(() => groupModelsByCompany(models), [models]);

  // 当前选中模型所属公司（用于选项栏初始定位与外部切换跟随）
  const valueCompanyId = models.find((m) => m.id === value)?.company?.id ?? null;
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(valueCompanyId);

  // 仅在所选模型的公司变化时跟随（用户自行切换选项卡浏览时不打断）
  useEffect(() => {
    if (valueCompanyId) setActiveCompanyId(valueCompanyId);
  }, [valueCompanyId]);

  if (models.length === 0) {
    return (
      <div>
        <label className="text-sm font-medium text-light-text mb-3 block">图片模型</label>
        <div className="text-sm text-light-muted bg-light-hover rounded-xl px-4 py-3">
          暂无可用的图片模型，请联系管理员配置
        </div>
      </div>
    );
  }

  const activeGroup = groups.find((g) => g.key === activeCompanyId) ?? groups[0];

  return (
    <div>
      <label className="text-sm font-medium text-light-text mb-3 block">图片模型</label>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {groups.map((group) => {
          const isActive = group.key === activeGroup?.key;
          return (
            <button
              key={group.key}
              type="button"
              onClick={() => {
                setActiveCompanyId(group.key);
                // 切到新公司时默认选中该公司第一个模型（当前模型已属该公司则不重复触发）
                const first = group.models[0];
                if (first && !group.models.some((m) => m.id === value)) {
                  onChange(first.id);
                }
              }}
              title={group.label}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 transition-all ${
                isActive
                  ? 'border-accent-blue bg-accent-blue/5 text-accent-blue'
                  : 'border-light-border bg-white text-light-text hover:border-accent-blue'
              }`}
            >
              <CompanyLogo companyId={group.key} label={group.label} size={16} />
              <span className="text-xs font-medium">{group.label}</span>
            </button>
          );
        })}
      </div>
      <div className="grid gap-2">
        {activeGroup?.models.map((model) => {
          const isActive = model.id === value;
          return (
            <button
              key={model.id}
              type="button"
              onClick={() => onChange(model.id)}
              className={`text-left px-3 py-2.5 rounded-xl border transition-all flex items-center justify-between gap-2 ${
                isActive
                  ? 'border-accent-blue bg-accent-blue/5 shadow-md'
                  : 'border-light-border bg-white hover:border-accent-blue'
              }`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <CompanyLogo
                    companyId={model.company?.id || activeGroup?.key || 'other'}
                    label={model.company?.label || activeGroup?.label || '其他'}
                    size={14}
                  />
                  <span
                    className={`font-medium text-sm truncate ${
                      isActive ? 'text-accent-blue' : 'text-light-text'
                    }`}
                  >
                    {model.name}
                  </span>
                </div>
                {model.description && (
                  <p className="text-xs text-light-muted mt-1">{model.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {model.availability && (
                  <span
                    title={
                      model.availability.total > 0
                        ? `近24小时可用率（不含审核拦截），样本 ${model.availability.total} 条`
                        : '近24小时无生成样本，默认显示 100%'
                    }
                    className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium ${availabilityTone(model.availability.rate)}`}
                  >
                    <Activity className="w-3 h-3" />
                    {model.availability.rate.toFixed(2)}%
                  </span>
                )}
                {isActive && <Check className="w-4 h-4 text-accent-blue" />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
